import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams, useBlocker, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { postsAPI, mediaAPI, socialAPI, aiAPI, getErrorMessage, type UploadProgressCallback } from '../../services/api';
import { useThemeStore } from '../../store/themeStore';
import { useAuthStore } from '../../store/authStore';
import type { ApplyTemplateResult } from '../../types/templates';
import { TemplateApplyPanel, TemplateQuickChips } from '../templates';
import { PLATFORMS } from './types';
import type { ContentType, MediaFile, UrlMedia, LinkPreview, ConnectedAccount, ScheduleConfig, StoryCard, ReelScriptBeat, CarouselSlide } from './types';
import { MediaSection } from './MediaSection';
import type { SavedMediaItemRef } from './SavedMediaTab';
import { SchedulePanel } from './SchedulePanel';
import { InternalComments } from './InternalComments';
import {
  buildMediaVariantsPayload,
  extractPlatformVariants,
} from './mediaVariantUtils';
// Lazy-load heavy content-type-specific editors so only the active one is
// fetched & mounted. Each carries its own media preview stack and was
// contributing to renderer memory pressure on the /compose/:id route.
const StoryCardsEditor = lazy(() => import('./StoryCardsEditor'));
const CarouselSlidesEditor = lazy(() => import('./CarouselSlidesEditor'));
const ReelScriptEditor = lazy(() => import('./ReelScriptEditor'));
import { NotionComposerShell, NotionCollapsible } from './notion';
import { MediaComplianceBanner } from './MediaComplianceBanner';
import { LayoutTipsPanel } from './PlatformMediaGrid';
import { PostCreationHistory } from './PostCreationHistory';
import ComposerCaptionTabs from './ComposerCaptionTabs';
import ComposerContentTypeRules from './ComposerContentTypeRules';
import ComposerAICommandCenter from './ComposerAICommandCenter';
import FirstCommentLimits from './FirstCommentLimits';

const COMPOSER_EDITOR_DRAFT_PREFIX = 'compose-editor-draft:';

interface ComposerEditorDraftSnapshot {
  title: string;
  caption: string;
  hashtags: string[];
  selectedPlatforms: string[];
  selectedAccounts: string[];
  scheduleConfig: {
    scheduleType: 'now' | 'later';
    scheduledAt?: string;
  };
  urlMediaItems: UrlMedia[];
  linkPreview: LinkPreview | null;
  contentType: ContentType;
  firstComment: string;
  storyLinkUrl: string;
  storyLinkLabel: string;
  reelCoverUrl: string;
  reelDurationSeconds: number | '';
  notificationMode: boolean;
  storyCards: StoryCard[];
  reelBeats: ReelScriptBeat[];
  carouselSlides: CarouselSlide[];
  mediaTab: 'saved' | 'library' | 'upload' | 'url' | 'link' | 'cloud' | 'ai';
  platformOverrides: Record<string, { text: string }>;
  activePlatformTab: string | null;
}

function makeComposerDraftKey(postId?: string): string {
  return `${COMPOSER_EDITOR_DRAFT_PREFIX}${postId || 'new'}:${Date.now()}`;
}

function normalizeUrlMediaItems(items: UrlMedia[]): UrlMedia[] {
  return items.map((item) => ({
    ...item,
    platformVariants: item.platformVariants || {},
  }));
}

export const ComposerPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { postId } = useParams<{ postId?: string }>();
  const [searchParams] = useSearchParams();
  const currentUser = useAuthStore((state) => state.user);
  const isEditing = !!postId;
  const queryClient = useQueryClient();
  const editorDraftKey = searchParams.get('draft_key');
  const editorReturnedMediaId = searchParams.get('editor_media_id');
  const editorApplyMode = searchParams.get('editor_apply_mode');
  const editorSlotIndex = searchParams.get('editor_slot_index');
  const editorBaseMediaId = searchParams.get('editor_base_media_id');
  const editorTargetPlatformsParam = searchParams.get('editor_target_platforms') || '';

  // Core state
  const [caption, setCaption] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({ scheduleType: 'now' });
  const [hashtags, setHashtags] = useState<string[]>([]);
  // Track which revision of the post we last loaded (prevents stale cache races)
  const [loadedRevision, setLoadedRevision] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  // Snapshot of saved state for dirty tracking
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');

  // Accessibility IDs
  const captionId = React.useId();
  const firstCommentId = React.useId();

  // Media state
  const [mediaTab, setMediaTab] = useState<'saved' | 'library' | 'upload' | 'url' | 'link' | 'cloud' | 'ai'>('library');
  const [urlMediaItems, setUrlMediaItems] = useState<UrlMedia[]>([]);
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);

  // Content type — post, story, reel, igtv, carousel, short
  const [contentType, setContentType] = useState<ContentType>('post');

  // First comment (Instagram, LinkedIn)
  const [firstComment, setFirstComment] = useState('');

  // Content-type specific fields
  const [storyLinkUrl, setStoryLinkUrl] = useState('');
  const [storyLinkLabel, setStoryLinkLabel] = useState('');
  const [reelCoverUrl, setReelCoverUrl] = useState('');
  const [reelDurationSeconds, setReelDurationSeconds] = useState<number | ''>('');
  const [notificationMode, setNotificationMode] = useState(false);
  const [storyCards, setStoryCards] = useState<StoryCard[]>([]);
  const [reelBeats, setReelBeats] = useState<ReelScriptBeat[]>([]);
  const [carouselSlides, setCarouselSlides] = useState<CarouselSlide[]>([]);
  const [generatingStory, setGeneratingStory] = useState(false);
  const [generatingReel, setGeneratingReel] = useState(false);
  const [generatingCarousel, setGeneratingCarousel] = useState(false);
  const [preflightErrors, setPreflightErrors] = useState<Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>>([]);
  const [preflightWarnings, setPreflightWarnings] = useState<Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>>([]);
  // Tracks whether the advisory preflight call failed on the last run.
  // We don't block the user on this, but we do surface it so they
  // know the "looks good" impression is not validated.
  const [preflightUnavailable, setPreflightUnavailable] = useState(false);
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [platformOverrides, setPlatformOverrides] = useState<Record<string, { text: string }>>({});
  // Active platform tab for the caption editor. Mirrors the Studio
  // pattern: each tab edits a single platform's override, and the
  // textarea is ALWAYS scoped to the active tab (never a shared
  // multi-platform blob).
  const [activePlatformTab, setActivePlatformTab] = useState<string | null>(null);
  const processedEditorDraftRef = React.useRef<string | null>(null);
  const urlMediaItemsRef = React.useRef<UrlMedia[]>([]);

  useEffect(() => {
    urlMediaItemsRef.current = urlMediaItems;
  }, [urlMediaItems]);

  // Debounced preflight validation for existing posts.
  // Sends current unsaved state so validation reflects the editor, not the last-saved version.
  useEffect(() => {
    if (!postId) { return; }
    const handle = setTimeout(async () => {
      try {
        const currentMediaIds = urlMediaItems
          .filter((item) => item.status === 'done' && item.mediaId)
          .map((item) => item.mediaId!);
        const mediaVariants = buildMediaVariantsPayload(
          normalizeUrlMediaItems(
            urlMediaItems.filter((item) => item.status === 'done' && item.mediaId),
          ),
        );
        // Build platform_overrides for preflight: { twitter: { text: "..." }, ... }
        const preflightOverrides: Record<string, { text: string }> = {};
        for (const [plat, val] of Object.entries(platformOverrides)) {
          if (val?.text) preflightOverrides[plat] = { text: val.text };
        }
        const r = await postsAPI.preflight(postId, {
          text: caption,
          media_ids: currentMediaIds,
          target_platforms: selectedPlatforms,
          content_type: contentType,
          ...(mediaVariants.length > 0 ? { media_variants: mediaVariants } : {}),
          ...(Object.keys(preflightOverrides).length > 0 ? { platform_overrides: preflightOverrides } : {}),
        });
        const data = r.data as {
          errors?: Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>;
          warnings?: Array<{ platform: string; message: string; code?: string; fixable?: boolean; fix_type?: string; media_id?: string }>;
        };
        setPreflightErrors(data.errors || []);
        setPreflightWarnings(data.warnings || []);
        setPreflightUnavailable(false);
      } catch {
        // Preflight is advisory, so we don't block publish — but we do
        // surface a "validation unavailable" badge so users know the
        // lack of errors isn't a green light.
        setPreflightUnavailable(true);
      }
    }, 800);
    return () => clearTimeout(handle);
  }, [postId, caption, contentType, selectedPlatforms, urlMediaItems, reelCoverUrl, reelDurationSeconds, storyLinkUrl, platformOverrides]);

  const showFirstComment = selectedPlatforms.some((id) => {
    const p = PLATFORMS.find((x) => x.id === id);
    return p?.supportsFirstComment;
  });

  // Pre-fill schedule from calendar navigation
  useEffect(() => {
    const scheduleParam = searchParams.get('schedule');
    if (scheduleParam && !isEditing) {
      const date = new Date(scheduleParam);
      if (!isNaN(date.getTime())) {
        setScheduleConfig({ scheduleType: 'later', scheduledAt: date });
      }
    }
  }, [searchParams, isEditing]);

  // Pre-fill caption from AI Content Writer "Use in Composer"
  useEffect(() => {
    const prefillCaption = searchParams.get('caption');
    const prefillHashtags = searchParams.get('hashtags');
    const prefillPlatforms = searchParams.get('platforms') || searchParams.get('target_platforms');
    if (prefillCaption && !isEditing) {
      setCaption(prefillCaption);
    }
    if (prefillHashtags && !isEditing) {
      setHashtags(prefillHashtags.split(',').map((h) => h.trim()).filter(Boolean));
    }
    if (prefillPlatforms && !isEditing) {
      setSelectedPlatforms(prefillPlatforms.split(',').map((p) => p.trim()).filter(Boolean));
    }
  }, [searchParams, isEditing]);

  // Seed media into a new composer session when coming from AI Image
  // Generator, Media Library, or an editor return flow.
  const seededIncomingMediaRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (isEditing) {
      return;
    }

    const incomingMediaId = searchParams.get('newMediaId') || searchParams.get('ai_media_id');
    const aiImageUrl = searchParams.get('ai_image_url');
    const seedKey = incomingMediaId || aiImageUrl;

    if (!seedKey || seededIncomingMediaRef.current === seedKey) {
      return;
    }
    seededIncomingMediaRef.current = seedKey;

    let cancelled = false;

    const appendMediaItem = (incoming: UrlMedia, preferredTab: 'saved' | 'url' = 'saved') => {
      if (cancelled) {
        return;
      }
      setUrlMediaItems((prev) => {
        const dedupeKey = incoming.mediaId || incoming.id;
        if (prev.some((item) => (item.mediaId || item.id) === dedupeKey)) {
          return prev;
        }
        return [...prev, incoming];
      });
      setMediaTab(preferredTab);
    };

    const seedIncomingMedia = async () => {
      if (incomingMediaId) {
        try {
          const response = await mediaAPI.get(incomingMediaId);
          const media = response.data as {
            id?: string;
            original_url?: string;
            thumbnail_url?: string | null;
            filename?: string;
            mime_type?: string;
            media_type?: string;
            alt_text?: string | null;
          };

          appendMediaItem(
            {
              id: media.id || incomingMediaId,
              url: media.original_url || '',
              filename: media.filename || `media-${incomingMediaId}`,
              contentType:
                media.mime_type ||
                (media.media_type === 'video' ? 'video/mp4' : 'image/jpeg'),
              type: media.media_type === 'video' ? 'video' : 'image',
              status: 'done',
              progress: 100,
              mediaId: media.id || incomingMediaId,
              altText: media.alt_text || t('comp_ai_generated_image', 'AI-generated image'),
            },
            'saved'
          );
          return;
        } catch {
          // If the lookup fails, fall through to URL-only seeding when possible.
        }
      }

      let validUrl = '';
      if (aiImageUrl) {
        try {
          const parsed = new URL(aiImageUrl);
          if (['http:', 'https:'].includes(parsed.protocol)) {
            validUrl = aiImageUrl;
          }
        } catch {
          // Invalid URL — skip.
        }
      }

      if (!validUrl) {
        return;
      }

      appendMediaItem(
        {
          id: incomingMediaId || `ai-${Date.now()}`,
          url: validUrl,
          filename: `ai-image-${incomingMediaId || Date.now()}.png`,
          contentType: 'image/png',
          type: 'image',
          status: 'done',
          progress: 100,
          mediaId: incomingMediaId || undefined,
          altText: t('comp_ai_generated_image', 'AI-generated image'),
        },
        incomingMediaId ? 'saved' : 'url'
      );
    };

    void seedIncomingMedia();

    return () => {
      cancelled = true;
    };
  }, [searchParams, isEditing, t]);

  const hydratePersistedMediaItem = useCallback(
    async (mediaId: string): Promise<UrlMedia> => {
      const response = await mediaAPI.get(mediaId);
      const media = response.data as {
        id?: string;
        original_url?: string;
        thumbnail_url?: string | null;
        filename?: string;
        mime_type?: string;
        media_type?: string;
        alt_text?: string | null;
      };

      return {
        id: media.id || mediaId,
        url: media.original_url || '',
        filename: media.filename || `media-${mediaId}`,
        contentType:
          media.mime_type ||
          (media.media_type === 'video' ? 'video/mp4' : 'image/jpeg'),
        type: media.media_type === 'video' ? 'video' : 'image',
        status: 'done',
        progress: 100,
        mediaId: media.id || mediaId,
        altText: media.alt_text || '',
        platformVariants: {},
      };
    },
    [],
  );

  const showEditorApplyUndoToast = useCallback(
    (message: string, previousItems: UrlMedia[]) => {
      toast.custom(
        (toastInstance) => (
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{message}</p>
              <p className="text-xs text-gray-500">
                {t('comp_editor_undo_hint', 'Undo this draft-only media change.')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setUrlMediaItems(normalizeUrlMediaItems(previousItems));
                toast.dismiss(toastInstance.id);
              }}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
            >
              {t('comp_editor_undo', 'Undo')}
            </button>
          </div>
        ),
        { duration: 7000 },
      );
    },
    [t],
  );

  useEffect(() => {
    if (!editorDraftKey) {
      return;
    }

    const effectKey = [
      editorDraftKey,
      editorReturnedMediaId || 'none',
      editorApplyMode || 'none',
      editorSlotIndex || 'none',
      editorBaseMediaId || 'none',
      editorTargetPlatformsParam || 'none',
    ].join(':');
    if (processedEditorDraftRef.current === effectKey) {
      return;
    }
    processedEditorDraftRef.current = effectKey;

    let cancelled = false;

    const clearEditorParams = () => {
      const nextParams = new URLSearchParams(searchParams.toString());
      [
        'draft_key',
        'editor_media_id',
        'editor_apply_mode',
        'editor_slot_index',
        'editor_base_media_id',
        'editor_target_platforms',
      ].forEach((key) => nextParams.delete(key));
      const nextSearch = nextParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true, state: { bypassDirty: true } },
      );
    };

    const restoreAndApply = async () => {
      let restoredItems = structuredClone(urlMediaItemsRef.current);
      const rawSnapshot = sessionStorage.getItem(editorDraftKey);
      if (rawSnapshot) {
        try {
          const snapshot = JSON.parse(rawSnapshot) as ComposerEditorDraftSnapshot;
          restoredItems = normalizeUrlMediaItems(snapshot.urlMediaItems || []);
          if (!cancelled) {
            setTitle(snapshot.title || '');
            setCaption(snapshot.caption || '');
            setHashtags(snapshot.hashtags || []);
            setSelectedPlatforms(snapshot.selectedPlatforms || []);
            setSelectedAccounts(snapshot.selectedAccounts || []);
            setScheduleConfig({
              scheduleType: snapshot.scheduleConfig?.scheduleType || 'now',
              scheduledAt: snapshot.scheduleConfig?.scheduledAt
                ? new Date(snapshot.scheduleConfig.scheduledAt)
                : undefined,
            });
            setUrlMediaItems(restoredItems);
            setLinkPreview(snapshot.linkPreview || null);
            setContentType(snapshot.contentType || 'post');
            setFirstComment(snapshot.firstComment || '');
            setStoryLinkUrl(snapshot.storyLinkUrl || '');
            setStoryLinkLabel(snapshot.storyLinkLabel || '');
            setReelCoverUrl(snapshot.reelCoverUrl || '');
            setReelDurationSeconds(snapshot.reelDurationSeconds ?? '');
            setNotificationMode(Boolean(snapshot.notificationMode));
            setStoryCards(snapshot.storyCards || []);
            setReelBeats(snapshot.reelBeats || []);
            setCarouselSlides(snapshot.carouselSlides || []);
            setMediaTab(snapshot.mediaTab || 'saved');
            setPlatformOverrides(snapshot.platformOverrides || {});
            setActivePlatformTab(snapshot.activePlatformTab || null);
          }
        } catch {
          // Ignore corrupt snapshots; the return flow still clears params below.
        } finally {
          sessionStorage.removeItem(editorDraftKey);
        }
      }

      if (!editorReturnedMediaId || !editorApplyMode) {
        if (!cancelled) {
          clearEditorParams();
        }
        return;
      }

      try {
        const hydratedItem = await hydratePersistedMediaItem(editorReturnedMediaId);
        if (cancelled) {
          return;
        }
        const previousItems = structuredClone(restoredItems);
        const parsedSlotIndex = Number(editorSlotIndex);
        const selectedVariantPlatforms = editorTargetPlatformsParam
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);

        const mutateTargetItem = (
          items: UrlMedia[],
          mutator: (item: UrlMedia) => UrlMedia,
        ): UrlMedia[] => {
          const byIndex = Number.isFinite(parsedSlotIndex)
            ? items.map((item, idx) => (idx === parsedSlotIndex ? mutator(item) : item))
            : items;
          if (Number.isFinite(parsedSlotIndex)) {
            return normalizeUrlMediaItems(byIndex);
          }
          return normalizeUrlMediaItems(
            items.map((item) =>
              item.mediaId === editorBaseMediaId ? mutator(item) : item,
            ),
          );
        };

        if (editorApplyMode === 'replace_current_media') {
          setUrlMediaItems((prev) =>
            mutateTargetItem(prev, (item) => ({
              ...hydratedItem,
              altText: item.altText,
              platformVariants: {},
            })),
          );
          showEditorApplyUndoToast(
            t(
              'comp_editor_replace_success',
              'Saved as an asset and updated the current media in this draft.',
            ),
            previousItems,
          );
        } else if (
          editorApplyMode === 'keep_original_use_for_selected_platforms' &&
          selectedVariantPlatforms.length > 0
        ) {
          setUrlMediaItems((prev) =>
            mutateTargetItem(prev, (item) => ({
              ...item,
              platformVariants: {
                ...(item.platformVariants || {}),
                ...Object.fromEntries(
                  selectedVariantPlatforms.map((platform) => [platform, hydratedItem.mediaId!]),
                ),
              },
            })),
          );
          showEditorApplyUndoToast(
            t(
              'comp_editor_variant_success',
              'Saved as an asset and applied this edited version to the selected platforms.',
            ),
            previousItems,
          );
        } else {
          toast.success(
            t(
              'comp_editor_library_only_success',
              'Saved as a reusable library asset. This draft was left unchanged.',
            ),
          );
        }
      } catch (error) {
        toast.error(
          getErrorMessage(
            error,
            t(
              'comp_editor_restore_failed',
              'Failed to restore the edited media into Composer',
            ),
          ),
        );
      } finally {
        if (!cancelled) {
          clearEditorParams();
        }
      }
    };

    void restoreAndApply();

    return () => {
      cancelled = true;
    };
  }, [
    editorApplyMode,
    editorBaseMediaId,
    editorDraftKey,
    editorReturnedMediaId,
    editorSlotIndex,
    editorTargetPlatformsParam,
    hydratePersistedMediaItem,
    location.pathname,
    navigate,
    searchParams,
    showEditorApplyUndoToast,
    t,
  ]);

  // Load existing post
  const { data: existingPost } = useQuery({
    queryKey: ['post', postId],
    queryFn: () => postsAPI.get(postId!).then((r) => r.data),
    enabled: !!postId,
  });

  useEffect(() => {
    if (!existingPost) { return; }
    // Use updated_at (or id for first load) as a revision marker so we
    // re-populate state whenever the server data changes (e.g. after save
    // + refetch) instead of relying on a boolean `loaded` flag that races
    // with React Query's stale-while-revalidate.
    const revision = existingPost.updated_at || existingPost.id || '';
    if (revision === loadedRevision) { return; }

    setTitle(existingPost.title || '');
    setCaption(existingPost.text || '');
    setHashtags(existingPost.hashtags || []);
    setSelectedAccounts(existingPost.target_accounts || []);
    setSelectedPlatforms(existingPost.target_platforms || []);
    if (existingPost.first_comment) { setFirstComment(existingPost.first_comment); }
    if (existingPost.platform_overrides && typeof existingPost.platform_overrides === 'object') {
      const loaded: Record<string, { text: string }> = {};
      for (const [plat, val] of Object.entries(existingPost.platform_overrides)) {
        if (val && typeof val === 'object' && 'text' in (val as Record<string, unknown>)) {
          loaded[plat] = { text: String((val as Record<string, unknown>).text || '') };
        }
      }
      setPlatformOverrides(loaded);
    }
    if (existingPost.reel_cover_url) { setReelCoverUrl(existingPost.reel_cover_url); }
    if (typeof existingPost.reel_duration_seconds === 'number') { setReelDurationSeconds(existingPost.reel_duration_seconds); }
    if (typeof existingPost.notification_mode === 'boolean') { setNotificationMode(existingPost.notification_mode); }
    const firstLink = (existingPost.story_stickers || []).find(
      (s: { type?: string; url?: string; label?: string }) => s.type === 'link'
    );
    if (firstLink) {
      if (firstLink.url) { setStoryLinkUrl(firstLink.url); }
      if (firstLink.label) { setStoryLinkLabel(firstLink.label); }
    }
    if (existingPost.content_type && ['post', 'story', 'reel', 'igtv', 'carousel', 'short'].includes(existingPost.content_type)) {
      setContentType(existingPost.content_type);
    }
    if (Array.isArray(existingPost.story_cards)) {
      setStoryCards(existingPost.story_cards as StoryCard[]);
    }
    if (Array.isArray(existingPost.reel_script)) {
      setReelBeats(existingPost.reel_script as ReelScriptBeat[]);
    }
    if (Array.isArray(existingPost.carousel_slides)) {
      setCarouselSlides(existingPost.carousel_slides as CarouselSlide[]);
    }
    if (existingPost.scheduled_at) {
      setScheduleConfig({ scheduleType: 'later', scheduledAt: new Date(existingPost.scheduled_at) });
    }

    const loadedUrlMedia: UrlMedia[] = (existingPost.media || []).map((m: { media_id?: string; url?: string; thumbnail_url?: string; mime_type?: string; media_type?: string; platform_versions?: Record<string, unknown> }, idx: number) => ({
        id: m.media_id || `existing-${idx}`,
        url: m.url || m.thumbnail_url || '',
        filename: m.url?.split('/').pop() || `media-${idx}`,
        contentType: m.mime_type || (m.media_type === 'video' ? 'video/mp4' : 'image/jpeg'),
        type: (m.media_type === 'video' ? 'video' : 'image') as 'image' | 'video',
        status: 'done' as const,
        progress: 100,
        mediaId: m.media_id || undefined,
        altText: existingPost.alt_texts?.[m.media_id || ''] || '',
        platformVariants: extractPlatformVariants(
          m.platform_versions,
          m.media_id || undefined,
        ),
      }));
    if (loadedUrlMedia.length > 0) {
      setUrlMediaItems(normalizeUrlMediaItems(loadedUrlMedia));
      if (loadedUrlMedia.length > 0) { setMediaTab('saved'); }
    } else {
      setUrlMediaItems([]);
    }

    if (existingPost.link_attachment) {
      setLinkPreview({
        url: existingPost.link_attachment.url || existingPost.link || '',
        title: existingPost.link_attachment.title || '',
        description: existingPost.link_attachment.description || '',
        image: existingPost.link_attachment.image || '',
        site_name: existingPost.link_attachment.site_name || '',
        favicon: existingPost.link_attachment.favicon || '',
        domain: existingPost.link_attachment.domain || '',
        link_type: existingPost.link_attachment.link_type || 'article',
      });
      if (!existingPost.media?.length) { setMediaTab('link'); }
    }

    // Save snapshot for dirty tracking
    const mediaIds = (existingPost.media || []).map((m: { media_id?: string }) => m.media_id || '').join(',');
    setSavedSnapshot(JSON.stringify({
      title: existingPost.title || '',
      caption: existingPost.text || '',
      hashtags: existingPost.hashtags || [],
      platforms: existingPost.target_platforms || [],
      accounts: existingPost.target_accounts || [],
      mediaIds,
      mediaVariants: loadedUrlMedia.map((item) => item.platformVariants || {}),
    }));

    setLoadedRevision(revision);
  }, [existingPost, loadedRevision]);

  // Fetch connected accounts
  const { data: connectedAccounts = [] } = useQuery({
    queryKey: ['connected-accounts'],
    queryFn: async () => {
      const r = await socialAPI.list();
      const accounts = r.data?.accounts || [];
      return accounts;
    },
  });

  const availableAccounts = useMemo(
    () => connectedAccounts.filter((acc: ConnectedAccount) => selectedPlatforms.includes(acc.platform)),
    [connectedAccounts, selectedPlatforms]
  );

  // Auto-select all available accounts when platforms change. Applies to
  // new posts AND edits where the saved draft has no explicit accounts
  // (legacy posts, AI-agent-generated drafts, posts where the user added
  // a new platform later). Without this, the Publish button stays disabled
  // even though the backend's publish route auto-resolves accounts from
  // target_platforms — producing a frustrating "looks ready but won't
  // publish" state. The `selectedAccounts.length === 0` guard ensures we
  // never overwrite an explicit user selection that was already loaded.
  useEffect(() => {
    if (availableAccounts.length > 0 && selectedAccounts.length === 0) {
      setSelectedAccounts(availableAccounts.map((acc: ConnectedAccount) => acc.id));
    }
  }, [availableAccounts, selectedAccounts.length]);

  // Character counts — use platform override text if available
  const charCountByPlatform = useMemo(() => {
    const counts: Record<string, { current: number; max: number }> = {};
    selectedPlatforms.forEach((platformId) => {
      const platform = PLATFORMS.find((p) => p.id === platformId);
      if (platform) {
        const effectiveText = platformOverrides[platformId]?.text ?? caption;
        counts[platformId] = { current: effectiveText.length, max: platform.maxChars };
      }
    });
    return counts;
  }, [caption, selectedPlatforms, platformOverrides]);

  // Platform / account toggles
  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId) ? prev.filter((p) => p !== platformId) : [...prev, platformId]
    );
  };

  // First-comment AI generation. Reuses `aiAPI.rewrite` with an
  // "engaging" tone hint so the model shifts the main caption into a
  // short, reply-friendly form suitable for the IG/LinkedIn first
  // comment (hashtags dump, CTA, or @-mentions).
  const generateFirstCommentMutation = useMutation({
    mutationFn: () =>
      aiAPI.rewrite({
        original_text: caption,
        target_platform: 'instagram',
        tone: 'engaging_short',
      }),
    onSuccess: (data) => {
      const text = data.data?.rewritten || '';
      if (text) {
        setFirstComment(text);
        toast.success(
          t('comp_first_comment_generated', 'First comment generated')
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, t('comp_first_comment_failed', 'Failed to generate first comment'))
      );
    },
  });

  // Upload + create post
  const uploadMediaMutation = useMutation({
    mutationFn: async () => {
      // Visible progress via a single updating toast — previously the
      // upload phase ran silently, which felt like the app had
      // hung. We update the same toast id per file so users see
      // "Uploading 2/3" tick up in real time, then clear it on
      // completion or error.
      const uploadedMedia: Array<{ media_id: string }> = [];
      const totalFiles = mediaFiles.length;
      if (totalFiles === 0) {
        return uploadedMedia;
      }
      const toastId = 'media-upload-progress';
      toast.loading(
        t('compose_uploading_count', 'Uploading {{i}}/{{n}}…', {
          i: 1,
          n: totalFiles,
        }),
        { id: toastId }
      );
      try {
        for (let i = 0; i < totalFiles; i++) {
          const mf = mediaFiles[i];
          // Skip re-upload when the file was already uploaded out-of-
          // band (e.g. the AI-describe button in MediaUploadTab
          // uploads eagerly to get a server media_id). Deduplicating
          // avoids doubling S3 storage + bandwidth on save.
          if (mf.mediaId) {
            uploadedMedia.push({ media_id: mf.mediaId });
            continue;
          }
          toast.loading(
            t('compose_uploading_count', 'Uploading {{i}}/{{n}}…', {
              i: i + 1,
              n: totalFiles,
            }),
            { id: toastId }
          );
          const onProgress: UploadProgressCallback = (loaded) => {
            const pct =
              mf.file.size > 0
                ? Math.min(100, Math.round((loaded / mf.file.size) * 100))
                : 0;
            toast.loading(
              t(
                'compose_uploading_count_pct',
                'Uploading {{i}}/{{n}} · {{pct}}%',
                { i: i + 1, n: totalFiles, pct }
              ),
              { id: toastId }
            );
          };
          const response = await mediaAPI
            .directUpload(mf.file, onProgress)
            .then((r) => r.data);
          uploadedMedia.push(response);
        }
        toast.dismiss(toastId);
      } catch (err) {
        toast.dismiss(toastId);
        throw err;
      }
      await new Promise((r) => setTimeout(r, 300));
      return uploadedMedia;
    },
  });

  const createPostMutation = useMutation({
    mutationFn: async (action: 'draft' | 'schedule' | 'publish') => {
      let uploadedMedia: { media_id: string }[] = [];
      if (mediaFiles.length > 0) {
        uploadedMedia = await uploadMediaMutation.mutateAsync();
      }

      // Don't append hashtags to text — they're stored separately in
      // the hashtags array and combined by the publisher at publish time.
      const finalCaption = caption;
      const completedSavedMedia = normalizeUrlMediaItems(
        urlMediaItems
          .filter((item) => item.status === 'done' && item.mediaId),
      );
      const urlMediaIds = completedSavedMedia
        .filter((item) => item.mediaId)
        .map((item) => item.mediaId!);
      const mediaVariants = buildMediaVariantsPayload(
        completedSavedMedia,
        uploadedMedia.length,
      );

      const altTexts: Record<string, string> = {};
      mediaFiles.forEach((mf, i) => {
        if (mf.altText && uploadedMedia[i]?.media_id) {
          altTexts[uploadedMedia[i].media_id] = mf.altText;
        }
      });
      urlMediaItems.forEach((um) => {
        if (um.altText && um.mediaId) altTexts[um.mediaId] = um.altText;
      });

      const postData: Record<string, unknown> = {
        title: title.trim() || null,
        text: finalCaption,
        media_ids: [...uploadedMedia.map((m: { media_id: string }) => m.media_id), ...urlMediaIds],
        hashtags,
        target_accounts: selectedAccounts,
        target_platforms: selectedPlatforms,
        content_type: contentType,
      };
      if (mediaVariants.length > 0) {
        postData.media_variants = mediaVariants;
      }

      // Include per-platform text overrides if any exist
      if (Object.keys(platformOverrides).length > 0) {
        const overrides: Record<string, { text: string }> = {};
        for (const [plat, val] of Object.entries(platformOverrides)) {
          if (val?.text && selectedPlatforms.includes(plat)) {
            overrides[plat] = { text: val.text };
          }
        }
        if (Object.keys(overrides).length > 0) {
          postData.platform_overrides = overrides;
        }
      }

      if (Object.keys(altTexts).length > 0) postData.alt_texts = altTexts;
      if (firstComment.trim()) postData.first_comment = firstComment.trim();

      if (contentType === 'story' && storyLinkUrl.trim()) {
        postData.story_stickers = [{
          type: 'link',
          url: storyLinkUrl.trim(),
          label: storyLinkLabel.trim() || undefined,
        }];
      }
      if ((contentType === 'reel' || contentType === 'short' || contentType === 'igtv')) {
        if (reelCoverUrl.trim()) postData.reel_cover_url = reelCoverUrl.trim();
        if (typeof reelDurationSeconds === 'number' && reelDurationSeconds > 0) {
          postData.reel_duration_seconds = reelDurationSeconds;
        }
      }
      if (notificationMode) postData.notification_mode = true;

      if (contentType === 'story' && storyCards.length > 0) {
        postData.story_cards = storyCards;
      }
      if ((contentType === 'reel' || contentType === 'short' || contentType === 'igtv') && reelBeats.length > 0) {
        postData.reel_script = reelBeats;
      }
      if (contentType === 'carousel' && carouselSlides.length > 0) {
        postData.carousel_slides = carouselSlides;
      }

      if (linkPreview) {
        postData.link = linkPreview.url;
        postData.link_attachment = {
          url: linkPreview.url,
          title: linkPreview.title,
          description: linkPreview.description,
          image: linkPreview.image,
          site_name: linkPreview.site_name,
          favicon: linkPreview.favicon,
          domain: linkPreview.domain,
          link_type: linkPreview.link_type,
        };
      }

      if (action === 'schedule' && scheduleConfig.scheduledAt) {
        postData.scheduled_at = scheduleConfig.scheduledAt.toISOString();
      }

      let resultPostId: string;
      if (isEditing && postId) {
        await postsAPI.update(postId, postData);
        resultPostId = postId;
      } else {
        const response = await postsAPI.create(postData);
        resultPostId = response.data?.post_id;
      }

      if (action === 'publish' && resultPostId) {
        await postsAPI.publishNow(resultPostId);
      }
      return resultPostId;
    },
    onSuccess: (_, action) => {
      const messages = {
        draft: isEditing ? t('comp_post_updated', 'Post updated') : t('comp_draft_saved', 'Draft saved successfully'),
        schedule: t('comp_post_scheduled', 'Post scheduled successfully'),
        publish: t('comp_post_published', 'Post published successfully'),
      };
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success(messages[action]);

      if (isEditing) {
        // Stay on the editor — just refetch so the UI shows saved state
        if (postId) {
          queryClient.invalidateQueries({ queryKey: ['post', postId] });
        }
      } else {
        // New post — navigate to the post list
        if (postId) {
          queryClient.removeQueries({ queryKey: ['post', postId] });
        }
        navigate('/posts');
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, isEditing ? t('comp_update_failed', 'Failed to update post') : t('comp_create_failed', 'Failed to create post')));
    },
  });

  // Validation
  const completedUrlMedia = useMemo(
    () => urlMediaItems.filter((item) => item.status === 'done'),
    [urlMediaItems]
  );

  const composerMediaContext = useMemo(
    () =>
      completedUrlMedia
        .map((item, index) => {
          const parts = [
            `media_${index + 1}`,
            item.type,
            item.filename || item.mediaId || item.id,
          ].filter(Boolean);
          return parts.join(' ');
        })
        .join('\n'),
    [completedUrlMedia]
  );

  const handleEditSavedMedia = useCallback(
    (item: SavedMediaItemRef) => {
      const persistedMediaId = item.mediaId;
      if (!persistedMediaId) {
        return;
      }

      const draftKey = makeComposerDraftKey(postId);
      const snapshot: ComposerEditorDraftSnapshot = {
        title,
        caption,
        hashtags,
        selectedPlatforms,
        selectedAccounts,
        scheduleConfig: {
          scheduleType: scheduleConfig.scheduleType,
          scheduledAt: scheduleConfig.scheduledAt?.toISOString(),
        },
        urlMediaItems: normalizeUrlMediaItems(urlMediaItems),
        linkPreview,
        contentType,
        firstComment,
        storyLinkUrl,
        storyLinkLabel,
        reelCoverUrl,
        reelDurationSeconds,
        notificationMode,
        storyCards,
        reelBeats,
        carouselSlides,
        mediaTab,
        platformOverrides,
        activePlatformTab,
      };
      sessionStorage.setItem(draftKey, JSON.stringify(snapshot));

      const params = new URLSearchParams({
        mediaId: persistedMediaId,
        returnTo: `${location.pathname}${location.search}`,
        source: 'compose',
        content_type: contentType,
        base_media_id: persistedMediaId,
        draft_key: draftKey,
      });

      if (selectedPlatforms.length > 0) {
        params.set('selected_platforms', selectedPlatforms.join(','));
      }
      if (typeof item.postMediaIndex === 'number') {
        params.set('slot_index', String(item.postMediaIndex));
      }

      const targetRoute =
        item.type === 'video' ? '/video-editor' : '/editor';
      navigate(`${targetRoute}?${params.toString()}`, {
        state: { bypassDirty: true },
      });
    },
    [
      activePlatformTab,
      caption,
      carouselSlides,
      contentType,
      firstComment,
      hashtags,
      linkPreview,
      location.pathname,
      location.search,
      mediaTab,
      navigate,
      notificationMode,
      platformOverrides,
      postId,
      reelBeats,
      reelCoverUrl,
      reelDurationSeconds,
      scheduleConfig,
      selectedAccounts,
      selectedPlatforms,
      storyCards,
      storyLinkLabel,
      storyLinkUrl,
      title,
      urlMediaItems,
    ]
  );

  const hasAnyMedia = mediaFiles.length > 0 || completedUrlMedia.length > 0;
  // Stable reference for the composer shell preview — avoids re-triggering
  // downstream media probes / memoized children on every ComposerPage render.
  const previewMediaItems = useMemo(
    () =>
      completedUrlMedia
        .filter((m) => m.url)
        .map((m) => ({ url: m.url, type: m.type })),
    [completedUrlMedia]
  );

  const isValid = useMemo(() => {
    return caption.trim().length > 0 || hasAnyMedia;
  }, [caption, hasAnyMedia]);

  const isPublishValid = useMemo(() => {
    const hasContent = caption.trim().length > 0 || hasAnyMedia;
    const hasSelectedPlatforms = selectedPlatforms.length > 0;
    const hasSelectedAccounts = selectedAccounts.length > 0;
    const charCountsValid = Object.values(charCountByPlatform).every((count) => count.current <= count.max);
    // Platform requirements must pass before we let the user hand the post
    // off to the scheduler — otherwise the Celery worker discovers the
    // problem hours later (e.g. "TikTok requires video content") and the
    // user has no idea why their post never went out.
    const preflightClean = preflightErrors.length === 0;
    return hasContent && hasSelectedPlatforms && hasSelectedAccounts && charCountsValid && preflightClean;
  }, [caption, hasAnyMedia, selectedPlatforms, selectedAccounts, charCountByPlatform, preflightErrors.length]);

  // Dirty tracking — compare current state against last-saved snapshot
  const isDirty = useMemo(() => {
    if (!isEditing) { return true; } // New post is always "dirty"
    if (!savedSnapshot) { return false; } // Not loaded yet
    const mediaIds = completedUrlMedia.map((m) => m.mediaId || '').join(',');
    const current = JSON.stringify({
      title,
      caption,
      hashtags,
      platforms: selectedPlatforms,
      accounts: selectedAccounts,
      mediaIds,
      mediaVariants: completedUrlMedia.map((item) => item.platformVariants || {}),
    });
    return current !== savedSnapshot;
  }, [isEditing, savedSnapshot, title, caption, hashtags, selectedPlatforms, selectedAccounts, completedUrlMedia]);

  // ── Unsaved-changes guards ──
  // 1. beforeunload — catches full-page reload / tab close. Modern
  //    browsers ignore the custom message and show a generic "Leave
  //    site?" prompt; setting returnValue triggers the prompt.
  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // 2. In-app nav blocker — now that the app runs on the data
  //    router (Phase 10), useBlocker catches SPA transitions too.
  //    We intentionally use window.confirm rather than a styled
  //    modal because React Router's block/unblock lifecycle is easy
  //    to get wrong with async dialogs.
  const navBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty
      && !nextLocation.state?.bypassDirty
      && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (navBlocker.state === 'blocked') {
      const ok = window.confirm(
        t(
          'comp_dirty_nav_confirm',
          'You have unsaved changes. Leave anyway?'
        )
      );
      if (ok) {
        navBlocker.proceed();
      } else {
        navBlocker.reset();
      }
    }
  }, [navBlocker, t]);

  // ── Preflight fix handlers ──
  const handleAutoFixMedia = useCallback(async (mediaId: string, platform: string, fixType: string) => {
    try {
      if (fixType === 're_encode') {
        const resp = await mediaAPI.reEncode(mediaId, platform, contentType);
        const data = resp.data as { task_id?: string; already_compliant?: boolean };
        if (data.already_compliant) {
          toast.success(t('preflight_already_ok', 'Media already meets requirements'));
        } else {
          toast.success(t('preflight_fix_started', 'Fix started — check the compliance panel below'));
        }
      } else {
        // aspect_ratio fix — use sync auto-fix
        const resp = await mediaAPI.autoFix(mediaId, platform, contentType, 'pad');
        const data = resp.data as { media_id?: string; url?: string; filename?: string; already_compliant?: boolean };
        if (data.already_compliant) {
          toast.success(t('preflight_already_ok', 'Media already meets requirements'));
          return;
        }
        if (data.media_id && data.url) {
          setUrlMediaItems((prev) =>
            prev.map((item) => {
              if (item.mediaId === mediaId) {
                return { ...item, id: `fixed-${data.media_id}`, url: data.url!, filename: data.filename || item.filename, mediaId: data.media_id! };
              }
              return item;
            })
          );
          toast.success(t('preflight_fixed', 'Media fixed for {{platform}}', { platform: platform.charAt(0).toUpperCase() + platform.slice(1) }));
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, t('preflight_fix_failed', 'Failed to fix media')));
    }
  }, [contentType, t, setUrlMediaItems]);

  const handleOpenAgentFix = useCallback((mediaId: string, platform: string, fixType: string) => {
    // Navigate to the AI agent chat with a pre-filled prompt to fix the media
    const prompt = fixType === 're_encode'
      ? `Compress the video (media ${mediaId}) to fit ${platform}'s file size limit`
      : `Fix the aspect ratio of media ${mediaId} for ${platform} using padding`;
    navigate(`/agent?prompt=${encodeURIComponent(prompt)}`);
  }, [navigate]);

  // Platform override handlers
  const handleOverrideChange = useCallback((platform: string, text: string) => {
    setPlatformOverrides((prev) => ({ ...prev, [platform]: { text } }));
  }, []);

  const handleOverridesReplace = useCallback((overrides: Record<string, { text: string }>) => {
    setPlatformOverrides((prev) => ({ ...prev, ...overrides }));
  }, []);

  const handleOverrideClear = useCallback((platform: string) => {
    setPlatformOverrides((prev) => {
      const next = { ...prev };
      delete next[platform];
      return next;
    });
  }, []);

  const handleTemplateApply = useCallback((result: ApplyTemplateResult) => {
    if (result.text) {
      setCaption(result.text);
    }
    if (result.first_comment) {
      setFirstComment(result.first_comment);
    }
    if (result.hashtags && result.hashtags.length > 0) {
      setHashtags(result.hashtags);
    }
    setShowTemplatePanel(false);
    toast.success(t('comp_template_applied', 'Template applied'));
  }, [t]);

  return (
    <div className="flex h-full w-full">
      <div className="flex-1 min-w-0">
        <NotionComposerShell
      isEditing={isEditing}
      contentType={contentType}
      title={title}
      onTitleChange={setTitle}
      selectedPlatforms={selectedPlatforms}
      platforms={PLATFORMS}
      isValid={isValid}
      isPublishValid={isPublishValid}
      isDirty={isDirty}
      isDraft={!isEditing || (existingPost?.status ?? 'draft') === 'draft'}
      postStatus={existingPost?.status ?? 'draft'}
      onSaveDraft={() => createPostMutation.mutate('draft')}
      onPublish={() => createPostMutation.mutate('publish')}
      onSchedule={() => createPostMutation.mutate('schedule')}
      isLoading={createPostMutation.isPending}
      showScheduleButton={scheduleConfig.scheduleType === 'later'}
      preflightErrors={preflightErrors}
      preflightWarnings={preflightWarnings}
      onAutoFixMedia={handleAutoFixMedia}
      onOpenAgentFix={handleOpenAgentFix}
      accountName={connectedAccounts[0]?.username || 'your_account'}
      _accountAvatarUrl={connectedAccounts[0]?.avatar_url}
      previewCaption={caption}
      previewPlatformCaptions={
        // Per-platform caption map so each phone mockup in the preview
        // column renders its own override text. Without this, every
        // phone rendered the shared caption — so a user who generated
        // a tailored Instagram version saw the shared copy on the
        // Instagram preview. Fall back happens in the shell: if a
        // platform has no override, the shell uses `previewCaption`.
        Object.fromEntries(
          Object.entries(platformOverrides)
            .filter(([, v]) => v?.text)
            .map(([k, v]) => [k, v.text])
        )
      }
      previewImageUrl={completedUrlMedia[0]?.url}
      previewMediaItems={previewMediaItems}
      mediaCount={completedUrlMedia.length}
    >
      {/* Content section with all the main editors and blocks */}
      <div className="space-y-4">
        {/* Preflight-unavailable badge — the server-side validator
            call failed on the last run. We don't block publishing
            (preflight is advisory) but we do let the user know the
            lack of errors isn't validated. */}
        {preflightUnavailable && (
          <div
            role="status"
            aria-live="polite"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            <span aria-hidden="true">⚠</span>
            <span>
              {t(
                'comp_preflight_unavailable',
                "Validation service unreachable — we couldn't run the pre-publish checks. Verify manually before posting."
              )}
            </span>
          </div>
        )}

        {/* Content-type rules panel — only renders for structured
            content types (story/reel/carousel/…). Standard "post"
            gets no panel because everything is free-form. */}
        <ComposerContentTypeRules
          contentType={contentType}
          mediaCount={mediaFiles.length + completedUrlMedia.length}
        />

        {/* Template Quick Chips */}
        <TemplateQuickChips
          currentText={caption}
          platform={selectedPlatforms[0]}
          onApply={handleTemplateApply}
        />

        {/* Caption Editor — per-platform tabs.
            Matches the AI Studio Post section: one tab per selected
            platform, textarea is always scoped to the active tab.
            The legacy single-textarea shared caption is gone because
            it encouraged users to glue `**Twitter:**`, `**Facebook:**`
            markers into one blob that then failed platform char
            limits. Discoverability win: the tabs + prompt make it
            obvious that the right-rail Platforms picker drives what
            the user is editing. */}
        <div>
          <label
            htmlFor={captionId}
            id={`${captionId}-label`}
            className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2"
          >
            {t('comp_post_caption', 'Post Caption')}
            {selectedPlatforms.length > 0 && (
              <span className="ml-2 normal-case text-slate-400 font-normal tracking-normal">
                {t(
                  'comp_caption_tabs_hint',
                  'One caption per platform — switch tabs to tailor each.'
                )}
              </span>
            )}
          </label>
          <ComposerCaptionTabs
            caption={caption}
            onCaptionChange={setCaption}
            selectedPlatforms={selectedPlatforms}
            onTogglePlatform={togglePlatform}
            platformOverrides={platformOverrides}
            onOverrideChange={handleOverrideChange}
            onOverridesReplace={handleOverridesReplace}
            onOverrideClear={handleOverrideClear}
            activeTab={activePlatformTab}
            onActiveTabChange={setActivePlatformTab}
            textareaId={captionId}
          />
        </div>

        {/* First Comment (Instagram/LinkedIn) */}
        {showFirstComment && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor={firstCommentId}
                className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                {t('comp_first_comment', 'First Comment')}
              </label>
              <button
                type="button"
                onClick={() => generateFirstCommentMutation.mutate()}
                disabled={!caption.trim() || generateFirstCommentMutation.isPending}
                aria-busy={generateFirstCommentMutation.isPending}
                title={
                  !caption.trim()
                    ? t('comp_first_comment_needs_caption', 'Write a caption first')
                    : t('comp_first_comment_generate_tip', 'Generate from caption')
                }
                className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                {generateFirstCommentMutation.isPending
                  ? t('comp_first_comment_generating', 'Generating…')
                  : t('comp_first_comment_generate', 'Generate with AI')}
              </button>
            </div>
            <textarea
              id={firstCommentId}
              value={firstComment}
              onChange={(e) => setFirstComment(e.target.value)}
              placeholder={t('comp_first_comment_placeholder', 'Add hashtags, mentions, or additional context as a first comment...')}
              aria-describedby={`${firstCommentId}-hint ${firstCommentId}-limits`}
              className="w-full h-20 p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-transparent resize-none text-sm leading-relaxed transition-colors"
            />
            <p id={`${firstCommentId}-hint`} className="sr-only">
              {t(
                'comp_first_comment_sr_hint',
                'First comment posted automatically after the main post. Supported on Instagram and LinkedIn.'
              )}
            </p>
            {/* P1-4 — mirror backend `_FIRST_COMMENT_LIMITS` per
                platform so users see over-limit warnings inline
                rather than waiting for the publish call to reject. */}
            <FirstCommentLimits
              id={`${firstCommentId}-limits`}
              text={firstComment}
              selectedPlatforms={selectedPlatforms}
            />
          </div>
        )}

        {/* Story cards editor */}
        {contentType === 'story' && (
          <NotionCollapsible
            title={t('comp_notion_story_cards', 'Story Cards')}
            count={storyCards.length}
            defaultOpen
          >
            <Suspense fallback={<div className="p-4 text-xs text-gray-400">{t('comp_loading', 'Loading…')}</div>}>
            <StoryCardsEditor
              cards={storyCards}
              onChange={setStoryCards}
              onGenerate={async () => {
                const topic = (caption || '').trim();
                if (!topic) {
                  toast.error(t('comp_ai_need_topic', 'Add a caption or topic first'));
                  return;
                }
                setGeneratingStory(true);
                try {
                  const r = await aiAPI.generateStoryCards({
                    topic,
                    platform: selectedPlatforms[0] || 'instagram',
                    card_count: Math.max(3, storyCards.length || 3),
                  });
                  const data = r.data as { cards?: StoryCard[] };
                  if (Array.isArray(data.cards) && data.cards.length > 0) {
                    const normalized: StoryCard[] = data.cards.map((c, i) => ({
                      index: typeof c.index === 'number' ? c.index : i,
                      media_index: c.media_index,
                      copy: c.copy || '',
                      background_color: c.background_color,
                      stickers: Array.isArray(c.stickers) ? c.stickers : [],
                    }));
                    setStoryCards(normalized);
                    toast.success(t('comp_story_cards_generated', 'Story cards generated!'));
                  }
                } catch (error: unknown) {
                  toast.error(getErrorMessage(error, t('comp_story_cards_failed', 'Story card generation failed')));
                } finally {
                  setGeneratingStory(false);
                }
              }}
              generating={generatingStory}
            />
            </Suspense>
          </NotionCollapsible>
        )}

        {/* Reel script editor */}
        {(contentType === 'reel' || contentType === 'short' || contentType === 'igtv') && (
          <NotionCollapsible
            title={t('comp_notion_reel_script', 'Script Beats')}
            count={reelBeats.length}
            defaultOpen
          >
            <Suspense fallback={<div className="p-4 text-xs text-gray-400">{t('comp_loading', 'Loading…')}</div>}>
            <ReelScriptEditor
              beats={reelBeats}
              onChange={setReelBeats}
              onGenerate={async () => {
                const topic = (caption || '').trim();
                if (!topic) {
                  toast.error(t('comp_ai_need_topic', 'Add a caption or topic first'));
                  return;
                }
                setGeneratingReel(true);
                try {
                  const r = await aiAPI.generateReelScript({
                    topic,
                    platform: selectedPlatforms[0] || 'instagram',
                    duration: typeof reelDurationSeconds === 'number' ? reelDurationSeconds : 30,
                  });
                  const data = r.data as { sections?: Array<{ timestamp?: string; text?: string; visual_direction?: string }>; duration_seconds?: number };
                  if (Array.isArray(data.sections) && data.sections.length > 0) {
                    const beats: ReelScriptBeat[] = data.sections.map((s) => ({
                      t: s.timestamp || '',
                      line: s.text || '',
                      overlay: s.visual_direction || undefined,
                    }));
                    setReelBeats(beats);
                    if (typeof data.duration_seconds === 'number') {
                      setReelDurationSeconds(data.duration_seconds);
                    }
                    toast.success(t('comp_reel_script_generated', 'Reel script generated!'));
                  }
                } catch (error: unknown) {
                  toast.error(getErrorMessage(error, t('comp_reel_script_failed', 'Reel script generation failed')));
                } finally {
                  setGeneratingReel(false);
                }
              }}
              generating={generatingReel}
            />
            </Suspense>
          </NotionCollapsible>
        )}

        {/* Carousel slides editor */}
        {contentType === 'carousel' && (
          <NotionCollapsible
            title={t('comp_notion_carousel_slides', 'Slides')}
            count={carouselSlides.length}
            defaultOpen
          >
            <Suspense fallback={<div className="p-4 text-xs text-gray-400">{t('comp_loading', 'Loading…')}</div>}>
            <CarouselSlidesEditor
              slides={carouselSlides}
              onChange={setCarouselSlides}
              onGenerate={async () => {
                const topic = (caption || '').trim();
                if (!topic) {
                  toast.error(t('comp_ai_need_topic', 'Add a caption or topic first'));
                  return;
                }
                setGeneratingCarousel(true);
                try {
                  const r = await aiAPI.generateCarouselSlides({
                    topic,
                    platform: selectedPlatforms[0] || 'instagram',
                    slide_count: Math.max(3, carouselSlides.length || 5),
                  });
                  const data = r.data as { slides?: CarouselSlide[] };
                  if (Array.isArray(data.slides) && data.slides.length > 0) {
                    const normalized: CarouselSlide[] = data.slides.map((s, i) => ({
                      index: typeof s.index === 'number' ? s.index : i,
                      media_index: s.media_index,
                      title: s.title || '',
                      body: s.body || '',
                    }));
                    setCarouselSlides(normalized);
                    toast.success(t('comp_carousel_generated', 'Carousel slides generated!'));
                  }
                } catch (error: unknown) {
                  toast.error(getErrorMessage(error, t('comp_carousel_failed', 'Carousel generation failed')));
                } finally {
                  setGeneratingCarousel(false);
                }
              }}
              generating={generatingCarousel}
            />
            </Suspense>
          </NotionCollapsible>
        )}

        {/* Story-specific: Link Sticker */}
        {contentType === 'story' && (
          <NotionCollapsible
            title={t('comp_notion_link_sticker', 'Link Sticker')}
            defaultOpen={false}
          >
            <div className="space-y-2">
              <label
                htmlFor="story-link-url"
                className="block text-[11px] font-medium text-slate-600 uppercase tracking-wide"
              >
                {t('comp_story_link_url_label', 'Link URL')}
              </label>
              <input
                id="story-link-url"
                type="url"
                value={storyLinkUrl}
                onChange={(e) => setStoryLinkUrl(e.target.value)}
                placeholder="https://example.com"
                aria-describedby="story-link-url-hint"
                className="w-full p-2 border border-slate-200 rounded-lg text-sm"
              />
              <p id="story-link-url-hint" className="sr-only">
                {t(
                  'comp_story_link_url_sr_hint',
                  'Destination URL users tap the sticker to open.'
                )}
              </p>

              <label
                htmlFor="story-link-cta"
                className="block text-[11px] font-medium text-slate-600 uppercase tracking-wide pt-2"
              >
                {t('comp_story_link_cta_label', 'Call-to-action text')}
              </label>
              <input
                id="story-link-cta"
                type="text"
                value={storyLinkLabel}
                onChange={(e) => setStoryLinkLabel(e.target.value)}
                placeholder={t('comp_story_link_cta', 'CTA text (e.g. Learn more)')}
                className="w-full p-2 border border-slate-200 rounded-lg text-sm"
              />
            </div>
          </NotionCollapsible>
        )}

        {/* Media section */}
        <NotionCollapsible
          title={t('comp_notion_media_block', 'Media')}
          count={completedUrlMedia.length}
          defaultOpen={false}
        >
          <MediaSection
            mediaTab={mediaTab}
            setMediaTab={setMediaTab}
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
            urlMediaItems={urlMediaItems}
            setUrlMediaItems={setUrlMediaItems}
            linkPreview={linkPreview}
            setLinkPreview={setLinkPreview}
            selectedPlatforms={selectedPlatforms}
            platforms={PLATFORMS}
            onEditSavedMedia={handleEditSavedMedia}
          />
        </NotionCollapsible>

        {/* Media compliance checker — detects issues and offers one-click auto-fix */}
        {completedUrlMedia.length > 0 && selectedPlatforms.length > 0 && (
          <MediaComplianceBanner
            urlMediaItems={urlMediaItems}
            setUrlMediaItems={setUrlMediaItems}
            selectedPlatforms={selectedPlatforms}
            setSelectedPlatforms={setSelectedPlatforms}
            contentType={contentType}
            setContentType={(ct) => setContentType(ct as ContentType)}
            hasPreflightErrors={preflightErrors.length > 0}
            onReplaceMedia={() => {
              if (typeof document !== 'undefined') {
                document
                  .getElementById('studio-media-section')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          />
        )}

        {/* Layout tips — suggest optimal image counts per platform */}
        {completedUrlMedia.length > 0 && selectedPlatforms.length > 0 && (
          <LayoutTipsPanel
            platform={selectedPlatforms[0]}
            currentCount={completedUrlMedia.length}
          />
        )}

        {/* Hashtags */}
        {hashtags.length > 0 && (
          <NotionCollapsible
            title={t('comp_suggested_hashtags', 'Suggested Hashtags')}
            count={hashtags.length}
            defaultOpen={false}
          >
            <div className="flex flex-wrap gap-1.5">
              {hashtags.map((hashtag, index) => (
                <span
                  key={index}
                  role="button"
                  tabIndex={0}
                  style={{ backgroundColor: theme.hex.primaryLight, color: theme.hex.primaryText }}
                  className="px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors border border-current border-opacity-10"
                  onClick={() => setCaption((prev) => (prev.includes(hashtag) ? prev : prev + ' ' + hashtag))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCaption((prev) => (prev.includes(hashtag) ? prev : prev + ' ' + hashtag)); } }}
                >
                  {hashtag}
                </span>
              ))}
            </div>
          </NotionCollapsible>
        )}

        {/* Scheduling */}
        <NotionCollapsible
          title={t('comp_notion_schedule', 'Schedule')}
          defaultOpen={false}
        >
          <SchedulePanel scheduleConfig={scheduleConfig} setScheduleConfig={setScheduleConfig} />
        </NotionCollapsible>

        {/* Internal Comments (when editing a post) */}
        {isEditing && postId && currentUser?.id && (
          <NotionCollapsible
            title={t('comp_notion_team_note', 'Team note')}
            defaultOpen={false}
          >
            <InternalComments postId={postId} currentUserId={currentUser.id} />
          </NotionCollapsible>
        )}

        {/* Creation history — replays the wizard flow that produced this post */}
        {isEditing && postId && (
          <NotionCollapsible
            title={t('comp_notion_creation_history', 'Creation history')}
            defaultOpen={false}
          >
            <PostCreationHistory postId={postId} />
          </NotionCollapsible>
        )}

        {/* AI Assistant */}
        <NotionCollapsible
          title={t('comp_ai_assistant', 'AI Assistant')}
          defaultOpen={false}
        >
          <ComposerAICommandCenter
            caption={caption}
            onCaptionChange={setCaption}
            hashtags={hashtags}
            onHashtagsChange={setHashtags}
            onFirstCommentChange={setFirstComment}
            selectedPlatforms={selectedPlatforms}
            contentType={contentType}
            mediaContext={composerMediaContext}
            onOverridesReplace={handleOverridesReplace}
            onOpenTemplates={() => setShowTemplatePanel(true)}
          />
        </NotionCollapsible>

        {/* Template Apply Panel */}
        <TemplateApplyPanel
          isOpen={showTemplatePanel}
          currentText={caption}
          onClose={() => setShowTemplatePanel(false)}
          platform={selectedPlatforms[0]}
          onApply={handleTemplateApply}
        />
      </div>
        </NotionComposerShell>
      </div>
    </div>
  );
};

export default ComposerPage;
